/** @jsx jsx */
import { useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import { isNil, omitBy } from 'lodash';
import useFormState from '@zapier/payment/src/utils/useFormState';
import PaymentFormButtons from '@zapier/payment/src/components/PaymentFormButtons';
import { AsyncOverlay } from '@zapier/payment/src/components/AsyncOverlay';
import { getValue } from '@zapier/payment/src/utils/formHelpers';
import { Alert, Text } from '@zapier/design-system';
import { Currency } from '@zapier/currency';
import {
  PaymentDetailsBank,
  PaymentProcessor,
  BankOwnershipType,
  PaymentSourceType,
  PaymentSourceInput,
  BankFormFields,
} from '@zapier/payment/src/types';
import BankAccountFormFields from './BankAccountFormFields';
import CreditCardErrors from '../CreditCardForm/CreditCardErrors';
import useBraintreeProcessor from './useBraintreeProcessor';

type Props = {
  /** The payment processor to use */
  paymentProcessor: PaymentProcessor;
  /** Key to connect with Braintree SDK */
  braintreeAPIKey: string;
  /** Error message to show */
  error?: string | null;
  /** Is currently saving */
  isEditingInFlight: boolean;
  /** Called when cancelling edit */
  onCancelEdit: () => void;
  /** Called if error is thrown */
  onError: (error: Error) => void;
  /** Called on form submit */
  onSave: (tokenDetails: PaymentSourceInput) => void;
  /** Currently saved bank details (if any) */
  savedAccount?: PaymentDetailsBank;
  /** What submethods of cards are supported? */
  supportedSubmethods: string[];
  /** The selected currency */
  selectedCurrency: Currency;
};
const rootStyle = css`
  display: grid;
  grid-gap: 20px;
`;

const BankAccountForm: React.FC<Props> = props => {
  const {
    state,
    updateErrors,
    checkIsValid,
    clearErrors,
    getFieldsErrors,
    startTokenization,
    stopTokenization,
    updateField,
  } = useFormState(PaymentSourceType.bank, props.savedAccount);
  const braintreeClient = useBraintreeProcessor(props.braintreeAPIKey);

  /**
   * Currently, ACH is our only submethod for the "bank" payment method type.
   * There is a future, where we will support other types, so I wanted to put
   * the scaffolding in here for when we eventually introduce those.
   */
  const isSubmethodSupported = props.supportedSubmethods.includes('ach');

  useEffect(() => {
    /**
     * Update our currency field. This is purely for displaying a useful error when a user has an unsupported payment method.
     */
    updateField(
      'currency',
      `${props.selectedCurrency.name} ${props.selectedCurrency.symbol}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedCurrency.name, props.selectedCurrency.symbol]);

  useEffect(() => {
    /**
     * Whenever we decide if we have a supported or unsupported payment method, update that in state so we can show a relevant error.
     */
    updateField(
      'isSubmethodSupported',
      isSubmethodSupported ? 'supported' : 'unsupported'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmethodSupported]);

  const hasServerError = !state.isDirty && props.error;
  const hasFormError = state.hasErrors;
  const submitText = 'Add Bank';
  const mandateText = `By clicking ["${submitText}"], I authorize Braintree, a service of PayPal, on behalf of Zapier (i) to verify my bank account information using bank information and consumer reports and (ii) to debit my bank account.`;
  const isBusy = props.isEditingInFlight || state.isTokenizing;

  const handleSave: React.FormEventHandler<HTMLFormElement> = async event => {
    event.preventDefault();
    const isValid = checkIsValid();

    if (!braintreeClient) {
      throw Error('Braintree client has not been initialized.');
    }

    const { fields } = state;
    const ownershipType = getValue('ownershipType', fields);
    const billingAddress = {
      streetAddress: getValue('streetAddress', fields),
      extendedAddress: getValue('extendedAddress', fields),
      locality: getValue('locality', fields),
      region: getValue('region', fields),
      postalCode: getValue('postalCode', fields),
    };
    const bankDetails = omitBy(
      {
        accountNumber: getValue('accountNumber', fields),
        routingNumber: getValue('routingNumber', fields),
        accountType: getValue('accountType', fields),
        ownershipType: getValue('ownershipType', fields),
        billingAddress,
        firstName:
          ownershipType === BankOwnershipType.personal
            ? getValue('firstName', fields)
            : null,
        lastName:
          ownershipType === BankOwnershipType.personal
            ? getValue('lastName', fields)
            : null,
        businessName:
          ownershipType === BankOwnershipType.business
            ? getValue('businessName', fields)
            : null,
      },
      isNil
    );

    if (isValid) {
      startTokenization();

      try {
        // @ts-ignore - Braintree types will be addressed later
        const token = await braintreeClient.tokenize({
          bankDetails,
          mandateText,
        });
        clearErrors();
        const result = {
          token: token.nonce,
          method: PaymentSourceType.bank,
          paymentProcessor: props.paymentProcessor,
          details: {
            ...token.details,
            address_city: billingAddress.locality,
            address_state: billingAddress.region,
            address_zip: billingAddress.postalCode,
            // Hardcoded to `US` - we only accept US bank accounts
            address_country: 'US',
          },
        };
        props.onSave(result);
      } catch (error) {
        clearErrors();
        props.onError(error);
      } finally {
        stopTokenization();
      }
    } else {
      updateErrors();
    }
  };

  return (
    <AsyncOverlay isVisible={isBusy}>
      <form
        css={rootStyle} // Used by E2E tests
        name="bank-form"
        onSubmit={handleSave}
      >
        {hasServerError ? (
          <Alert type="error">{props.error}</Alert>
        ) : (
          // @ts-ignore
          hasFormError && <CreditCardErrors errors={getFieldsErrors()} />
        )}

        <BankAccountFormFields
          /**
           * I don't like doing typecasts here, but until our state.fields can be refactored to correctly infer what kind
           * of fields we have here, this will have to do.
           */
          fields={state.fields as BankFormFields}
          onChangeField={updateField}
        />

        <Text type="altText800">
          It will take at least 3 business days for your bank payment to settle.
          During this time you will not be able to update or change your
          subscription.
        </Text>

        <Text type="altText800">{mandateText}</Text>

        <PaymentFormButtons
          buttonText={submitText}
          canSubmit={state.isDirty}
          hasCancelButton={true}
          isBusy={isBusy || !braintreeClient}
          onCancel={props.onCancelEdit}
        />
      </form>
    </AsyncOverlay>
  );
};

export default BankAccountForm;
